import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, Flag, CalendarRange } from "lucide-react";
import { listSalesOrders, scheduleSOLine } from "@/lib/api";
import { Button } from "@/components/ui/button";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Return Monday of the week containing `d`
const startOfWeek = (d) => {
  const nd = new Date(d);
  nd.setHours(0, 0, 0, 0);
  const dow = nd.getDay(); // 0=Sun..6=Sat
  const diff = dow === 0 ? -6 : 1 - dow; // to Monday
  nd.setDate(nd.getDate() + diff);
  return nd;
};

const addDays = (d, n) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
};

const shortDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

const OrdersKanban = () => {
  const qc = useQueryClient();
  const today = useMemo(() => new Date(), []);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [pendingCol, setPendingCol] = useState(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["orders", "kanban"],
    queryFn: async () => {
      const [c, p] = await Promise.all([
        listSalesOrders({ status: "confirmed" }),
        listSalesOrders({ status: "in_progress" }),
      ]);
      return [...(c || []), ...(p || [])];
    },
    refetchInterval: 5000,
  });

  const scheduleMut = useMutation({
    mutationFn: ({ orderId, lineId, scheduledDate }) => scheduleSOLine(orderId, lineId, scheduledDate),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Schedule updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  // Build cards: one per open (not-short-closed, balance > 0) line
  const cards = useMemo(() => {
    const arr = [];
    orders.forEach((o) => {
      (o.lines || []).forEach((l, idx) => {
        if (l.short_closed_at) return;
        const bal = (l.ordered_kg || 0) - (l.delivered_kg || 0);
        if (bal <= 0) return;
        arr.push({
          uid: `${o.id}::${l.line_id}`,
          order_id: o.id,
          line_id: l.line_id,
          line_idx: idx,
          order_number: o.order_number,
          customer_name: o.customer_name,
          priority: o.priority,
          target_delivery_date: o.target_delivery_date,
          sku_name: l.sku_name,
          ordered_kg: l.ordered_kg,
          delivered_kg: l.delivered_kg || 0,
          scheduled_date: l.scheduled_date || "",
        });
      });
    });
    return arr;
  }, [orders]);

  const weekISOs = useMemo(() => Array.from({ length: 7 }, (_, i) => toISO(addDays(weekStart, i))), [weekStart]);

  const byDay = useMemo(() => {
    const groups = { unscheduled: [] };
    weekISOs.forEach((iso) => (groups[iso] = []));
    cards.forEach((c) => {
      if (c.scheduled_date && weekISOs.includes(c.scheduled_date)) {
        groups[c.scheduled_date].push(c);
      } else {
        groups.unscheduled.push(c);
      }
    });
    return groups;
  }, [cards, weekISOs]);

  const handleDropOnCol = (targetIso) => {
    const uid = window.__kanbanDrag;
    if (!uid) return;
    const card = cards.find((c) => c.uid === uid);
    if (!card) return;
    const newDate = targetIso || "";
    if (card.scheduled_date === newDate) { setPendingCol(null); return; }
    scheduleMut.mutate({ orderId: card.order_id, lineId: card.line_id, scheduledDate: newDate });
    setPendingCol(null);
  };

  // Wire card drag start to global for simplicity
  const columns = [
    { key: "unscheduled", title: "Unscheduled", subtitle: null, tone: "amber", cards: byDay.unscheduled, iso: "" },
    ...weekISOs.map((iso, i) => ({
      key: iso, title: DAY_LABELS[i], subtitle: shortDate(iso),
      tone: iso === toISO(today) ? "amber" : "slate",
      cards: byDay[iso], iso,
    })),
  ];

  const weekLabel = `${shortDate(weekISOs[0])} — ${shortDate(weekISOs[5])}`;

  return (
    <div data-testid="orders-kanban" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarRange className="h-5 w-5 text-slate-700" />
          <p className="font-heading text-lg font-bold text-slate-900">Week of {weekLabel}</p>
          {scheduleMut.isPending && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" data-testid="kanban-prev-week" onClick={() => setWeekStart((d) => addDays(d, -7))}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
          </Button>
          <Button variant="outline" size="sm" data-testid="kanban-this-week" onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</Button>
          <Button variant="outline" size="sm" data-testid="kanban-next-week" onClick={() => setWeekStart((d) => addDays(d, 7))}>
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {isLoading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

      {!isLoading && (
        <div className="overflow-x-auto pb-3">
          <div className="grid grid-cols-[minmax(180px,1fr)_repeat(7,minmax(140px,1fr))] gap-2" onDragEnd={() => { window.__kanbanDrag = null; }}>
            {columns.map((col) => (
              <div
                key={col.key}
                data-testid={`kanban-col-${col.key}`}
                onDragOver={(e) => { e.preventDefault(); setPendingCol(col.key); }}
                onDrop={(e) => { e.preventDefault(); handleDropOnCol(col.iso); }}
                className={`flex flex-col rounded-sm border ${col.tone === "amber" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"} min-w-[160px] ${
                  pendingCol === col.key ? "ring-2 ring-slate-900" : ""
                }`}
              >
                <div className="px-2.5 py-2 border-b border-slate-200 rounded-t-sm bg-inherit">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{col.title}</p>
                  {col.subtitle && <p className="text-[11px] font-mono text-slate-700">{col.subtitle}</p>}
                  <p className="text-[10px] text-slate-500 font-mono">
                    {col.cards.length} · {col.cards.reduce((s, c) => s + Math.max(0, (c.ordered_kg || 0) - (c.delivered_kg || 0)), 0)} kg
                  </p>
                </div>
                <div className="flex-1 p-2 space-y-1.5 min-h-[220px]">
                  {col.cards.map((c) => (
                    <div
                      key={c.uid}
                      draggable
                      onDragStart={(e) => { window.__kanbanDrag = c.uid; try { e.dataTransfer.setData("text/plain", c.uid); } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("dataTransfer failed", err); } }}
                      data-testid={`kanban-card-${c.order_number}-${c.line_idx}`}
                      className="bg-white border border-slate-200 rounded-sm p-2 cursor-grab active:cursor-grabbing hover:border-slate-900 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0">
                          <p className="font-mono text-[10px] tracking-widest text-slate-500 font-semibold">{c.order_number}</p>
                          <p className="font-heading text-sm font-bold text-slate-900 truncate">{c.customer_name}</p>
                        </div>
                        {c.priority === "urgent" && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] tracking-wider uppercase font-bold px-1 py-0.5 rounded-sm bg-red-600 text-white shrink-0">
                            <Flag className="h-2.5 w-2.5" />
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center justify-between text-xs">
                        <span className="font-mono font-semibold text-slate-900 truncate mr-1">{c.sku_name}</span>
                        <span className="font-mono text-amber-700 shrink-0">{Math.max(0, (c.ordered_kg || 0) - (c.delivered_kg || 0))} kg</span>
                      </div>
                      {c.target_delivery_date && (
                        <p className="text-[10px] text-slate-500 mt-1">Due {shortDate(c.target_delivery_date)}</p>
                      )}
                    </div>
                  ))}
                  {col.cards.length === 0 && (
                    <p className="text-[11px] text-slate-400 text-center py-4 italic">Drop here</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersKanban;
